/**
 * MusicTrackMenu - 曲の「…」ボタンとそのドロップダウンメニュー
 *
 * 曲一覧・アルバム詳細・プレイヤーシートのどこから開いても同じ項目を表示する。
 * Header 右上のメニューと同じ DropdownMenu ベースで、ボタン自体がトリガーになる。
 */

import {
  ArrowDownToLine,
  Disc3,
  Ellipsis,
  ListEnd,
  ListPlus,
  ListStart,
  ListX,
  Mic,
  Star,
  Text,
  Trash2,
} from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useEpisodeDownload } from '@/hooks/useEpisodeDownload'
import { isEpisodeOffline } from '@/lib/db'
import { cn } from '@/lib/utils'
import { PATHS } from '@/routes/paths'
import { type MusicTrack, useMusicStore } from '@/store/useMusicStore'

import { ACCENT_COLOR, DOWNLOADED_ONLY_QUERY, MUSIC_DOMAIN, MUSIC_MIME_TYPE } from './constants'
import { toOfflineMusicMeta } from './offline-music'
import { useLyricsImport } from './use-lyrics-import'
import { usePlaylistPicker } from './use-playlist-picker'

type Props = {
  track: MusicTrack
  /** トリガーボタンの見た目を差し替える */
  className?: string
  iconSize?: number
  /** ダウンロード済みライブラリから開いた場合、遷移先も同じ絞り込みを引き継ぐ */
  downloadedOnly?: boolean
  /** プレイリスト詳細から開いた場合、そのプレイリストから外す操作を出す */
  onRemoveFromPlaylist?: () => void
}

export const MusicTrackMenu = ({
  track,
  className,
  iconSize = 20,
  downloadedOnly = false,
  onRemoveFromPlaylist,
}: Props) => {
  const navigate = useNavigate()
  const { downloadEpisode, removeOfflineEpisode } = useEpisodeDownload()
  const [isDownloaded, setIsDownloaded] = useState(false)
  const isFavorite = useMusicStore((s) => s.favoriteEpisodeIds.includes(track.episodeId))
  const toggleFavorite = useMusicStore((s) => s.toggleFavorite)
  const playTrackNext = useMusicStore((s) => s.playTrackNext)
  const addTrackToQueue = useMusicStore((s) => s.addTrackToQueue)
  const setPlayerOpen = useMusicStore((s) => s.setPlayerOpen)
  const openPlaylistPicker = usePlaylistPicker((s) => s.openPlaylistPicker)
  const openLyricsImport = useLyricsImport((s) => s.openLyricsImport)

  // プレイヤーシートから開いた場合は、遷移先が隠れないようシートも閉じる
  const navigateTo = (path: string) => {
    setPlayerOpen(false)
    navigate(downloadedOnly ? `${path}?${DOWNLOADED_ONLY_QUERY}` : path)
  }

  // 一覧の全行で常時問い合わせると重いため、メニューを開いたときだけ保存状況を確認する
  const handleOpenChange = (open: boolean) => {
    if (!open) return
    isEpisodeOffline(track.episodeId)
      .then(setIsDownloaded)
      .catch(() => setIsDownloaded(false))
  }

  const handleDownload = () => {
    void downloadEpisode({
      domain: MUSIC_DOMAIN,
      episodeId: track.episodeId,
      mimeType: MUSIC_MIME_TYPE,
      music: toOfflineMusicMeta(track),
      productId: track.productId,
      title: track.title,
    })
  }

  const handleRemoveDownload = () => {
    void removeOfflineEpisode(track.episodeId).then(() => setIsDownloaded(false))
  }

  return (
    <DropdownMenu onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="その他の操作"
          onClick={(e) => e.stopPropagation()}
          className={cn('shrink-0 border-none p-1 text-muted-foreground', className)}
        >
          <Ellipsis size={iconSize} />
        </button>
      </DropdownMenuTrigger>
      {/* プレイヤーシート(react-modal-sheet)は非常に大きい z-index を持つため、その上に重ねる */}
      <DropdownMenuContent align="end" collisionPadding={8} className="z-[10000000] w-60">
        <DropdownMenuItem onClick={() => toggleFavorite(track.episodeId)}>
          <Star
            size={18}
            style={isFavorite ? { color: ACCENT_COLOR } : undefined}
            fill={isFavorite ? ACCENT_COLOR : 'none'}
          />
          {isFavorite ? 'お気に入りから外す' : 'お気に入りに追加'}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => playTrackNext(track)}>
          <ListStart size={18} />
          次に再生
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => addTrackToQueue(track)}>
          <ListEnd size={18} />
          キューの最後に追加
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            setPlayerOpen(false)
            openPlaylistPicker([track.episodeId])
          }}
        >
          <ListPlus size={18} />
          プレイリストに追加
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            setPlayerOpen(false)
            openLyricsImport({ episodeId: track.episodeId, title: track.title })
          }}
        >
          <Text size={18} />
          歌詞をサイトから追加
        </DropdownMenuItem>
        {onRemoveFromPlaylist && (
          <DropdownMenuItem variant="destructive" onClick={onRemoveFromPlaylist}>
            <ListX size={18} />
            プレイリストから削除
          </DropdownMenuItem>
        )}
        {isDownloaded ? (
          <DropdownMenuItem variant="destructive" onClick={handleRemoveDownload}>
            <Trash2 size={18} />
            ダウンロードを削除
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onClick={handleDownload}>
            <ArrowDownToLine size={18} />
            ダウンロード
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => navigateTo(PATHS.MUSIC.ALBUM_DETAIL.replace(':product_id', String(track.productId)))}
        >
          <Disc3 size={18} />
          アルバムへ移動
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={!track.artistTagId}
          onClick={() => {
            if (!track.artistTagId) return
            navigateTo(PATHS.MUSIC.ARTIST_DETAIL.replace(':tag_id', String(track.artistTagId)))
          }}
        >
          <Mic size={18} />
          アーティストへ移動
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
